import React, { createContext, useContext, useCallback, useState } from 'react';
import { cacheService, DEFAULT_CACHE_TTL, CACHE_KEYS } from '../services/cache';

// Create Cache Context
const CacheContext = createContext();

// Custom hook to use cache context
export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

// Cache Provider Component
export const CacheProvider = ({ children }) => {
  const [cacheStats, setCacheStats] = useState({ memory: { size: 0 }, localStorage: { size: 0 } });
  const [refreshing, setRefreshing] = useState(new Set());

  // Update cache stats
  const updateCacheStats = useCallback(() => {
    setCacheStats(cacheService.getStats());
  }, []);

  // Generic cached API call wrapper
  const cachedApiCall = useCallback(async (
    cacheKey,
    apiFunction,
    ttl = DEFAULT_CACHE_TTL.projects,
    forceRefresh = false
  ) => {
    setRefreshing(prev => new Set(prev).add(cacheKey));
    
    try {
      const result = await cacheService.cachedApiCall(cacheKey, apiFunction, ttl, forceRefresh);
      updateCacheStats();
      return result;
    } finally {
      setRefreshing(prev => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
  }, [updateCacheStats]);

  // Specific cache operations for different data types
  const cacheOperations = {
    // Projects cache operations
    getProjects: useCallback(async (userId, apiFunction, forceRefresh = false) => {
      const cacheKey = CACHE_KEYS.PROJECTS(userId);
      return cachedApiCall(cacheKey, apiFunction, DEFAULT_CACHE_TTL.projects, forceRefresh);
    }, [cachedApiCall]),

    // Runs cache operations
    getRuns: useCallback(async (userId, apiFunction, forceRefresh = false) => {
      const cacheKey = CACHE_KEYS.RUNS(userId);
      return cachedApiCall(cacheKey, apiFunction, DEFAULT_CACHE_TTL.runs, forceRefresh);
    }, [cachedApiCall]),

    // Project details cache operations
    getProjectDetails: useCallback(async (projectId, userId, apiFunction, forceRefresh = false) => {
      const cacheKey = CACHE_KEYS.PROJECT_DETAILS(projectId, userId);
      return cachedApiCall(cacheKey, apiFunction, DEFAULT_CACHE_TTL.projectDetails, forceRefresh);
    }, [cachedApiCall]),

    // Organizations cache operations
    getOrganizations: useCallback(async (userId, apiFunction, forceRefresh = false) => {
      const cacheKey = CACHE_KEYS.ORGANIZATIONS(userId);
      return cachedApiCall(cacheKey, apiFunction, DEFAULT_CACHE_TTL.organizations, forceRefresh);
    }, [cachedApiCall]),

    // API Keys cache operations
    getApiKeys: useCallback(async (userId, apiFunction, forceRefresh = false) => {
      const cacheKey = CACHE_KEYS.API_KEYS(userId);
      return cachedApiCall(cacheKey, apiFunction, DEFAULT_CACHE_TTL.apiKeys, forceRefresh);
    }, [cachedApiCall])
  };

  // Cache management operations
  const cacheManagement = {
    // Clear specific user's cache
    clearUserCache: useCallback((userId) => {
      cacheService.clearUserCache(userId);
      updateCacheStats();
    }, [updateCacheStats]),

    // Clear all cache
    clearAllCache: useCallback(() => {
      cacheService.clearAll();
      updateCacheStats();
    }, [updateCacheStats]),

    // Invalidate cache by pattern
    invalidatePattern: useCallback((pattern) => {
      cacheService.invalidatePattern(pattern);
      updateCacheStats();
    }, [updateCacheStats]),

    // Force refresh specific cache entry
    refreshCache: useCallback(async (cacheKey, apiFunction, ttl) => {
      return cachedApiCall(cacheKey, apiFunction, ttl, true);
    }, [cachedApiCall]),

    // Get cache statistics
    getCacheStats: useCallback(() => {
      updateCacheStats();
      return cacheStats;
    }, [cacheStats, updateCacheStats]),

    // Check if data is being refreshed
    isRefreshing: useCallback((cacheKey) => {
      return refreshing.has(cacheKey);
    }, [refreshing])
  };

  // Context value
  const contextValue = {
    // Cache operations for different data types
    ...cacheOperations,
    
    // Cache management functions
    ...cacheManagement,
    
    // Cache state
    cacheStats,
    refreshing: Array.from(refreshing),
    
    // Utility functions
    generateCacheKey: cacheService.generateCacheKey.bind(cacheService),
    
    // Direct access to cache service for advanced usage
    cacheService
  };

  return (
    <CacheContext.Provider value={contextValue}>
      {children}
    </CacheContext.Provider>
  );
};

// HOC for components that need cache
export const withCache = (Component) => {
  return function CachedComponent(props) {
    return (
      <CacheProvider>
        <Component {...props} />
      </CacheProvider>
    );
  };
};

// Hook for cache-aware API calls with loading states
export const useCachedApiCall = (cacheKey, apiFunction, dependencies = [], options = {}) => {
  const { cachedApiCall } = useCache();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const {
    ttl = DEFAULT_CACHE_TTL.projects,
    forceRefresh = false,
    onSuccess,
    onError
  } = options;

  const fetchData = useCallback(async (forceRefreshOverride = forceRefresh) => {
    if (!cacheKey || !apiFunction) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await cachedApiCall(cacheKey, apiFunction, ttl, forceRefreshOverride);
      setData(result);
      
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      setError(err);
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [cacheKey, apiFunction, cachedApiCall, ttl, forceRefresh, onSuccess, onError]);

  // Auto-fetch on dependencies change
  React.useEffect(() => {
    fetchData();
  }, dependencies);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(true),
    fetchData
  };
};

export default CacheContext; 